import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Account, Connection, BudgetStatusResponse, GoalStatusResponse } from '../utils/api';
import { PROMPT_CATEGORIES } from '../config/promptLibrary';
import { isLiability } from '../utils/accounts';
import { budgetStatusColor, goalStatusColor, STATUS_COLORS } from '../utils/statusColors';
import './Sidebar.css';

// ── Types ──────────────────────────────────────────────────────────────────
interface ChatSession {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

interface NavItem {
  key: string;
  label: string;
  icon: string;
  subItems?: SubItem[];
}

interface SubItem {
  key: string;
  label: string;
}

interface SidebarProps {
  accounts: Account[];
  connections: Connection[];
  sessions: ChatSession[];
  activeSessionId: number | null;
  activeContentTitle: string;
  budgetStatus: BudgetStatusResponse | null;
  budgetLoading: boolean;
  budgetError: boolean;
  goalStatus: GoalStatusResponse | null;
  goalLoading: boolean;
  goalError: boolean;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onSessionSelect: (id: number) => void;
  onNewChat: () => void;
  onConnectBank: () => void;
  onManageConnections: () => void;
  onContentTitleChange: (title: string) => void;
  onAskAssistant: (message: string) => void;
}

// ── Nav sections ───────────────────────────────────────────────────────────
const NAV_SECTIONS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Navigation',
    items: [
      { key: 'accounts', label: 'Accounts', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m12-12l-2 2m-2-2l-7 7-7-7' },
      { key: 'chats', label: 'Chats', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
    ],
  },
  {
    label: 'Planning',
    items: [
      { key: 'budget', label: 'Budget', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
      { key: 'goals', label: 'Goals', icon: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9' },
    ],
  },
  {
    label: 'Assistant',
    items: [
      { key: 'quickActions', label: 'Quick Actions', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
    ],
  },
  // Monitoring (Unusual Activities) — temporarily disabled until the feature is implemented.
  // {
  //   label: 'Monitoring',
  //   items: [
  //     { key: 'unusual', label: 'Unusual Activities', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
  //   ],
  // },
];

// ── Account helpers ───────────────────────────────────────────────────────
// Liability balances (credit/loan/mortgage) are money owed — display negative.
const formatBalance = (acc: Account): string => {
  const bal = Math.abs(Number(acc.balance));
  const fmt = bal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return isLiability(acc.account_type) ? `-$${fmt}` : `$${fmt}`;
};

// Chat prompt sent when an account row is clicked. Includes the Plaid account
// ID (when available) so the assistant filters the exact account rather than
// matching by name, which can collide across institutions.
const accountTransactionsPrompt = (acc: Account): string => {
  const scope = acc.plaid_account_id ? ` (account ID: ${acc.plaid_account_id})` : '';
  return (
    `List all transactions from the last 30 days for my "${acc.account_name}" account${scope}. ` +
    'Show each transaction with its date, merchant, amount, and category, ' +
    'and include the total number of transactions and the total amount at the end.'
  );
};

// ── Institution grouping ──────────────────────────────────────────────────
// Sort rank so rows of the same type sit together within an institution:
// depository first, then credit, investment, loan; unknown types last.
// Legacy manual types (checking, savings) rank with depository.
const typeRank = (t: string): number => {
  const order: Record<string, number> = {
    depository: 0, checking: 0, savings: 0,
    credit: 1,
    investment: 2, brokerage: 2,
    loan: 3, mortgage: 3,
  };
  return order[t.toLowerCase()] ?? 4;
};

interface InstitutionGroup {
  key: string;
  name: string;
  logo: string | null;
  color: string | null;
  accounts: Account[];
}

const groupAccountsByInstitution = (
  accounts: Account[],
  connections: Connection[]
): InstitutionGroup[] => {
  const byConnection = new Map<number | 'other', Account[]>();
  const connectionIds = new Set(connections.map(c => c.id));
  for (const acc of accounts) {
    const id = acc.access_token_id;
    const key = id != null && connectionIds.has(id) ? id : 'other';
    const bucket = byConnection.get(key);
    if (bucket) bucket.push(acc);
    else byConnection.set(key, [acc]);
  }

  // Institutions in connections order; unmatched accounts last under "Other".
  const buckets: { key: string; name: string; logo: string | null; color: string | null; accounts: Account[] }[] = [];
  for (const conn of connections) {
    const accs = byConnection.get(conn.id);
    if (accs) {
      buckets.push({
        key: String(conn.id),
        name: conn.institution_name || 'Unknown Bank',
        logo: conn.institution_logo,
        color: conn.institution_color,
        accounts: accs,
      });
    }
  }
  const other = byConnection.get('other');
  if (other) buckets.push({ key: 'other', name: 'Other Accounts', logo: null, color: null, accounts: other });

  // Stable sort keeps the original order within the same type
  for (const bucket of buckets) {
    bucket.accounts.sort((a, b) => typeRank(a.account_type) - typeRank(b.account_type));
  }
  return buckets;
};

// ── Goal helpers ────────────────────────────────────────────────────────────

const fmtMoney = (n: number): string =>
  `$${Math.round(Math.abs(n)).toLocaleString('en-US')}`;

// Like fmtMoney but keeps the sign — for account_balance, which can be negative
// (an overdrawn funding account). Goal amounts (saved/target/backed) are always >= 0.
const fmtSigned = (n: number): string =>
  `${n < 0 ? '-' : ''}$${Math.round(Math.abs(n)).toLocaleString('en-US')}`;

const fmtMonthYear = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const typeLabel = (t: string): string => {
  const map: Record<string, string> = {
    checking: 'Checking', savings: 'Savings', credit: 'Credit',
    investment: 'Investment', loan: 'Loan', mortgage: 'Mortgage', other: 'Other',
  };
  return map[t.toLowerCase()] ?? t;
};

const AccountTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const t = type.toLowerCase();
  if (t === 'credit')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
  if (t === 'investment')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>;
  if (t === 'loan' || t === 'mortgage')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>;
};

// ── Inline SVG helper (thin-stroked) ───────────────────────────────────────
const Icon: React.FC<{ path: string }> = ({ path }) => (
  <svg className="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={path} />
  </svg>
);

// ── Collapse / expand icons ("|<" and ">|") ───────────────────────────────
const CollapseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="6" y1="5" x2="6" y2="19" />
    <polyline points="17 6 11 12 17 18" />
  </svg>
);

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="7 6 13 12 7 18" />
    <line x1="18" y1="5" x2="18" y2="19" />
  </svg>
);

// ── Chevron ────────────────────────────────────────────────────────────────
const Chevron: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <span className={`sidebar-chevron${expanded ? ' sidebar-chevron--open' : ''}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </span>
);

// ── Main Sidebar ───────────────────────────────────────────────────────────
const Sidebar: React.FC<SidebarProps> = ({
  accounts,
  connections,
  sessions,
  activeSessionId,
  activeContentTitle,
  budgetStatus,
  budgetLoading,
  budgetError,
  goalStatus,
  goalLoading,
  goalError,
  collapsed,
  onCollapsedChange,
  onSessionSelect,
  onNewChat,
  onConnectBank,
  onManageConnections,
  onContentTitleChange,
  onAskAssistant,
}) => {
  const { user } = useAuth();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const displayName = user ? `${user.first_name} ${user.last_name}`.trim() : 'Account';
  const initials = user
    ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
    : null;

  const avatarContent = initials ?? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );

  const asideRef = useRef<HTMLElement>(null);
  const expandBtnRef = useRef<HTMLButtonElement>(null);
  const collapseBtnRef = useRef<HTMLButtonElement>(null);
  const prevCollapsed = useRef(collapsed);

  const isMobileDrawer = () => window.matchMedia('(max-width: 768px)').matches;

  // On mobile the sidebar is an overlay drawer — close it after the user makes
  // a selection so the chat comes back into focus.
  const closeOnMobile = () => {
    if (isMobileDrawer()) onCollapsedChange(true);
  };

  // Hand focus to the matching toggle when the sidebar opens/closes, so
  // keyboard users are never left on an element that just became hidden.
  useEffect(() => {
    if (prevCollapsed.current === collapsed) return;
    prevCollapsed.current = collapsed;
    if (collapsed) {
      if (isMobileDrawer()) {
        // On mobile the collapsed rail is display:none, so focusing the rail's
        // expand button would strand focus on a hidden element. Hand focus back
        // to the header hamburger that controls the drawer instead.
        (document.querySelector('.mobile-menu-btn') as HTMLElement | null)?.focus();
      } else {
        expandBtnRef.current?.focus();
      }
    } else {
      collapseBtnRef.current?.focus();
    }
  }, [collapsed]);

  // Drawer behavior on mobile while expanded: Escape closes, Tab is
  // contained. The collapsed icon rail is in normal flow — no trap needed.
  const handleSidebarKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (collapsed || !isMobileDrawer()) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCollapsedChange(true);
      return;
    }
    if (e.key === 'Tab' && asideRef.current) {
      const focusables = asideRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [collapsed, onCollapsedChange]);

  const toggleSection = useCallback((key: string) => {
    setExpandedKey(prev => (prev === key ? null : key));
  }, []);

  const handleSubItemClick = useCallback((_key: string, label: string) => {
    onContentTitleChange(label);
  }, [onContentTitleChange]);

  const statusColor = budgetStatusColor;

  // Net worth = assets minus liabilities. Loan/mortgage balances count as
  // debt too, not just credit cards — matches the backend's net_worth rule.
  const netWorth = accounts.reduce((sum, acc) => {
    const bal = Number(acc.balance);
    return sum + (isLiability(acc.account_type) ? -Math.abs(bal) : bal);
  }, 0);

  const institutionGroups = useMemo(
    () => groupAccountsByInstitution(accounts, connections),
    [accounts, connections]
  );

  const renderAccountRow = (acc: Account) => {
    const type = acc.account_type.toLowerCase();
    return (
      // Redis-cached accounts carry no DB id — key off plaid_account_id first
      <button
        type="button"
        className={`sidebar-account-row sidebar-account-row--${type}`}
        key={acc.plaid_account_id || acc.id}
        onClick={() => { onAskAssistant(accountTransactionsPrompt(acc)); closeOnMobile(); }}
        title={`Show last 30 days of transactions for ${acc.account_name}`}
      >
        <div className={`sidebar-account-icon sidebar-account-icon--${type}`}>
          <AccountTypeIcon type={acc.account_type} />
        </div>
        <div className="sidebar-account-info">
          <span className="sidebar-account-name">{acc.account_name}</span>
          <span className={`sidebar-account-badge sidebar-account-badge--${type}`}>
            {typeLabel(acc.account_type)}
          </span>
        </div>
        <span className={`sidebar-account-bal${isLiability(type) ? ' sidebar-account-bal--neg' : ''}`}>{formatBalance(acc)}</span>
      </button>
    );
  };

  return (
    <>
      {/* Overlay for mobile — pointer affordance only; keyboard users close via Escape */}
      {!collapsed && <div className="sidebar-overlay" onClick={() => onCollapsedChange(true)} aria-hidden="true" />}

      <aside
        ref={asideRef}
        id="app-sidebar"
        className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}
        aria-label="Sidebar navigation"
        onKeyDown={handleSidebarKeyDown}
      >
        {collapsed ? (
          /* ── Collapsed icon rail ── */
          <div className="sidebar-rail">
            <button
              ref={expandBtnRef}
              className="sidebar-rail-btn"
              onClick={() => onCollapsedChange(false)}
              aria-label="Expand sidebar"
              title="Expand sidebar"
            >
              <ExpandIcon />
            </button>

            <button
              className="sidebar-rail-btn sidebar-rail-btn--accent"
              onClick={onNewChat}
              aria-label="New chat"
              title="New chat"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>

            {/* One icon per nav section — clicking expands the sidebar on that section */}
            {NAV_SECTIONS.flatMap(section => section.items).map(item => (
              <button
                key={item.key}
                className="sidebar-rail-btn"
                onClick={() => {
                  setExpandedKey(item.key);
                  onCollapsedChange(false);
                }}
                aria-label={item.label}
                title={item.label}
              >
                <Icon path={item.icon} />
              </button>
            ))}

            <div className="sidebar-rail-spacer" />

            <div className="sidebar-profile-avatar" aria-hidden="true" title={displayName}>
              {avatarContent}
            </div>
          </div>
        ) : (
        <>
        {/* ── Top bar: collapse button ── */}
        <div className="sidebar-topbar">
          <button
            ref={collapseBtnRef}
            className="sidebar-collapse-btn"
            onClick={() => onCollapsedChange(true)}
            aria-label={isMobileDrawer() ? 'Close menu' : 'Collapse sidebar'}
            title={isMobileDrawer() ? 'Close menu' : 'Collapse sidebar'}
          >
            <CollapseIcon />
          </button>
        </div>

        {/* ── Scrollable content ── */}
        <div className="sidebar-scroll">
          {/* ── New Chat CTA ── */}
          <button className="sidebar-new-chat-btn" onClick={() => { onNewChat(); closeOnMobile(); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>New Chat</span>
          </button>

          {/* ── Accordion nav sections ── */}
          {NAV_SECTIONS.map(section => (
            <div className="sidebar-group" key={section.label}>
              <h2 className="sidebar-group-label">{section.label}</h2>

              {section.items.map(item => {
                const isExpanded = expandedKey === item.key;

                return (
                  <div className="sidebar-accordion" key={item.key}>
                    {/* Top-level accordion header */}
                    <button
                      className={`sidebar-accordion-header${isExpanded ? ' sidebar-accordion-header--expanded' : ''}`}
                      onClick={() => toggleSection(item.key)}
                      aria-expanded={isExpanded}
                      aria-controls={`sidebar-section-${item.key}`}
                    >
                      <Icon path={item.icon} />
                      <span className="sidebar-accordion-label">{item.label}</span>
                      <Chevron expanded={isExpanded} />
                    </button>

                    {/* Expanded sub-items */}
                    <div
                      id={`sidebar-section-${item.key}`}
                      className={`sidebar-accordion-body${isExpanded ? ' sidebar-accordion-body--open' : ''}`}
                    >
                      {item.key === 'accounts' && (
                        <div className="sidebar-accounts-panel">
                          {accounts.length === 0 ? (
                            <div className="sidebar-accounts-empty">
                              <svg className="sidebar-accounts-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>
                              </svg>
                              <p>No accounts connected</p>
                              <button className="sidebar-accounts-cta" onClick={() => { onConnectBank(); closeOnMobile(); }}>
                                Connect a bank
                              </button>
                            </div>
                          ) : (
                            <>
                              {/* ── Net worth summary ── */}
                              <div className="sidebar-net-worth">
                                <span className="sidebar-net-worth-label">Net Worth</span>
                                <span className={`sidebar-net-worth-value${netWorth < 0 ? ' sidebar-net-worth-value--neg' : ''}`}>
                                  {netWorth < 0 ? '-' : ''}${Math.abs(netWorth).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </span>
                              </div>

                              {/* ── Accounts grouped by institution, then type ── */}
                              <div className="sidebar-account-list">
                                {institutionGroups.map(group => {
                                  // Sole unmatched bucket → flat list, no header
                                  const showHeader = !(group.key === 'other' && institutionGroups.length === 1);
                                  return (
                                  <div
                                    className={`sidebar-fi-group${showHeader ? '' : ' sidebar-fi-group--no-header'}`}
                                    key={group.key}
                                  >
                                    {showHeader && (
                                      <div className="sidebar-fi-header">
                                        {group.logo ? (
                                          <img
                                            className="sidebar-fi-logo"
                                            src={`data:image/png;base64,${group.logo}`}
                                            alt=""
                                          />
                                        ) : (
                                          <span
                                            className="sidebar-fi-avatar"
                                            style={group.color ? { background: group.color } : undefined}
                                            aria-hidden="true"
                                          >
                                            {group.name.charAt(0).toUpperCase()}
                                          </span>
                                        )}
                                        <span className="sidebar-fi-name">{group.name}</span>
                                      </div>
                                    )}
                                    {group.accounts.map(renderAccountRow)}
                                  </div>
                                  );
                                })}
                              </div>
                            </>
                          )}

                          {/* ── Action buttons ── */}
                          <div className="sidebar-account-actions">
                            <button
                              className="sidebar-action-btn sidebar-action-btn--primary"
                              onClick={() => { onConnectBank(); closeOnMobile(); }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                              </svg>
                              Connect Bank
                            </button>
                            <button className="sidebar-action-btn" onClick={() => { onManageConnections(); closeOnMobile(); }}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
                              </svg>
                              Manage Connections
                            </button>
                          </div>
                        </div>
                      )}

                      {item.key === 'chats' && (
                        <>
                          {/* Sub-items for sessions */}
                          {sessions.map(session => (
                            <button
                              key={session.id}
                              className={`sidebar-subitem${session.id === activeSessionId ? ' sidebar-subitem--active' : ''}`}
                              aria-current={session.id === activeSessionId ? 'true' : undefined}
                              onClick={() => {
                                onSessionSelect(session.id);
                                handleSubItemClick(`session-${session.id}`, session.title);
                                closeOnMobile();
                              }}
                            >
                              <span className="sidebar-subitem-dot" />
                              <span className="sidebar-subitem-truncate">{session.title}</span>
                            </button>
                          ))}
                        </>
                      )}

                      {item.key === 'budget' && (
                        <>
                          {budgetLoading ? (
                            <div className="sidebar-budget-loading">
                              <span className="sidebar-budget-loading-spinner" />
                              Loading…
                            </div>
                          ) : budgetError ? (
                            <div className="sidebar-subitem sidebar-subitem--error" role="alert">
                              Couldn't load budgets. They'll retry on your next visit.
                            </div>
                          ) : !budgetStatus || budgetStatus.budget_count === 0 ? (
                            <div className="sidebar-subitem sidebar-subitem--muted">
                              No budgets set up yet.
                            </div>
                          ) : (
                            <>
                              {budgetStatus.categories.slice(0, 3).map(cat => {
                                const statusText = cat.status === 'over_budget' ? 'Over' : cat.status === 'warning' ? 'Warn' : 'OK';
                                return (
                                  <div key={cat.category_name} className={`sidebar-budget-item sidebar-budget-item--${cat.status}`}>
                                    <div className="sidebar-budget-item-name">{cat.display_name}</div>
                                    <div className="sidebar-budget-item-bar">
                                      <div className="sidebar-budget-item-fill" style={{width: `${Math.min(cat.percent_used, 100)}%`, backgroundColor: statusColor(cat.status)}} />
                                    </div>
                                    <div className="sidebar-budget-item-status">{cat.percent_used.toFixed(0)}% • {statusText}</div>
                                  </div>
                                );
                              })}
                              {budgetStatus.categories.length > 3 && (
                                <button
                                  type="button"
                                  className="sidebar-budget-more"
                                  onClick={() => { onAskAssistant(
                                    'Show me my complete budget status for this month. List every budget category I have set up — all of them, not just the top few — with the monthly limit, amount spent so far, amount remaining, and whether each one is on track, near its limit, or over budget.'
                                  ); closeOnMobile(); }}
                                  title="Ask the assistant to show all budgets"
                                >
                                  +{budgetStatus.categories.length - 3} more budgets
                                </button>
                              )}
                            </>
                          )}
                        </>
                      )}

                      {item.key === 'goals' && (
                        <>
                          {goalLoading ? (
                            <div className="sidebar-budget-loading">
                              <span className="sidebar-budget-loading-spinner" />
                              Loading…
                            </div>
                          ) : goalError ? (
                            <div className="sidebar-subitem sidebar-subitem--error" role="alert">
                              Couldn't load goals. They'll retry on your next visit.
                            </div>
                          ) : (
                            <>
                              {(!goalStatus || goalStatus.goal_count === 0) ? (
                                <div className="sidebar-subitem sidebar-subitem--muted">
                                  No goals yet.
                                </div>
                              ) : (
                                <>
                                  {goalStatus.goals.map(g => {
                                    const color = g.is_complete ? STATUS_COLORS.info : goalStatusColor(g.status);
                                    // Complete on the savings schedule but the account doesn't yet
                                    // hold the full amount → say so explicitly rather than a flat
                                    // "Achieved" (no orphan asterisk footnote).
                                    const statusText = g.is_complete
                                      ? (g.underfunded ? '✓ Reached (underfunded)' : '✓ Achieved')
                                      : g.status === 'behind'
                                        ? `-${fmtMoney(g.shortfall)} behind`
                                        : '✓ On track';
                                    const statusTitle = g.is_complete && g.underfunded
                                      ? `Saved on schedule, but only ${fmtMoney(g.backed_amount)} of ${fmtMoney(g.target_amount)} is currently in ${g.account_name}`
                                      : undefined;
                                    return (
                                      <div key={g.id} className={`sidebar-goal-item sidebar-goal-item--${g.is_complete ? 'complete' : g.status}`}>
                                        <div className="sidebar-goal-item-head">
                                          <span className="sidebar-goal-item-name">{g.name}</span>
                                          <span className="sidebar-goal-item-status" style={{ color }} title={statusTitle}>{statusText}</span>
                                        </div>
                                        <div className={`sidebar-goal-item-account${g.underfunded ? ' sidebar-goal-item-account--low' : ''}`}>
                                          {g.account_name} · {g.account_goal_count > 1
                                            ? `${fmtMoney(g.backed_amount)} of ${fmtSigned(g.account_balance)}`
                                            : fmtSigned(g.account_balance)}
                                        </div>
                                        <div className="sidebar-goal-item-bar">
                                          <div className="sidebar-goal-item-fill" style={{ width: `${Math.min(g.progress_percent, 100)}%`, backgroundColor: color }} />
                                        </div>
                                        <div className="sidebar-goal-item-meta">
                                          <span>{fmtMoney(g.saved_amount)} / {fmtMoney(g.target_amount)}</span>
                                          <span>{g.is_complete ? 'Complete' : `ETA ${fmtMonthYear(g.projected_achievement_date)}`}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}

                      {item.key === 'quickActions' && (
                        <div className="sidebar-prompts-panel">
                          {PROMPT_CATEGORIES.map(cat => (
                            <div className="sidebar-prompt-group" key={cat.key}>
                              <div className="sidebar-prompt-group-label">
                                <Icon path={cat.icon} />
                                <span>{cat.label}</span>
                              </div>
                              {cat.prompts.map(prompt => (
                                <button
                                  type="button"
                                  className="sidebar-prompt-btn"
                                  key={prompt}
                                  onClick={() => { onAskAssistant(prompt); closeOnMobile(); }}
                                  title="Send to the assistant"
                                >
                                  {prompt}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}

                      {item.key === 'unusual' && (
                        <>
                          <button
                            className="sidebar-subitem"
                            onClick={() => handleSubItemClick('unusual-recent', 'Recent Unusual Activity')}
                          >
                            <span className="sidebar-subitem-dot" />
                            <span>Recent Activity</span>
                          </button>
                          <button
                            className="sidebar-subitem"
                            onClick={() => handleSubItemClick('unusual-history', 'Unusual Activity History')}
                          >
                            <span className="sidebar-subitem-dot" />
                            <span>History</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* ── Pinned user profile row ── */}
        <div className="sidebar-profile">
          <div className="sidebar-profile-avatar" aria-hidden="true">
            {avatarContent}
          </div>
          <div className="sidebar-profile-info">
            <div className="sidebar-profile-name">
              <span className="sidebar-profile-name-text">{displayName}</span>
              {user?.tier_name && (
                <span
                  className={`sidebar-tier-badge tier-${user.tier_name.toLowerCase()}`}
                >
                  {user.tier_name}
                </span>
              )}
            </div>
            {user && <div className="sidebar-profile-email">{user.email}</div>}
          </div>
        </div>
        </>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
